import { useRef, useState } from "react";
import { Camera, Loader2, CheckCircle2, Film } from "lucide-react";
import { PhotoNoteModal } from "@/components/PhotoNoteModal";

/**
 * Inline-Foto-Button (Klingel) für TC-Themen.
 * Variants:
 *  - `variant="pill"`: kompakter Foto-Button + kleiner Video-Button (Film-Icon).
 *  - `variant="card"`: ganzflächige Karte (Klick öffnet Foto). Video als zweiter
 *    klein-Button rechts oben.
 * Wenn bereits Aufnahmen existieren, wird der Button grün ausgefüllt mit Häkchen + Anzahl.
 *
 * Sub-Buttons öffnen jeweils die Kamera (capture="environment") –
 * Foto-Button mit accept="image/*", Video-Button mit accept="video/*" capture="environment".
 */
export const ThemePhotoButton = ({
  projectId,
  category,
  description = "",
  label = "Foto",
  onUploaded,
  size = "md",
  testid,
  existingCount = 0,
  variant = "pill",
}) => {
  const photoRef = useRef(null);
  const videoRef = useRef(null);
  const [pickedFile, setPickedFile] = useState(null);
  const [pickedType, setPickedType] = useState("photo");
  const [modalOpen, setModalOpen] = useState(false);
  const uploading = false;

  const handleFile = (type) => (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setPickedFile(file);
    setPickedType(type);
    setModalOpen(true);
  };

  const onModalClose = () => {
    setModalOpen(false);
    setPickedFile(null);
  };

  const onModalUploaded = () => {
    onUploaded?.();
  };

  const sizes = {
    sm: "h-9 px-3 text-[13px] gap-1.5",
    md: "h-10 px-3.5 text-[14px] gap-1.5",
    lg: "h-11 px-4 text-[14px] gap-2",
  };

  const isDone = existingCount > 0;

  // CARD variant: full label, photo button takes the row, small video button below
  if (variant === "card") {
    return (
      <>
        <div className="flex gap-2">
          <button
            type="button"
            data-testid={testid || `photo-btn-${category}`}
            onClick={() => photoRef.current?.click()}
            disabled={uploading}
            className={`flex-1 min-h-[60px] px-3.5 py-2.5 rounded-xl font-semibold transition active:scale-[0.98] disabled:opacity-50 border-2 flex items-center gap-2.5 text-left ${
              isDone
                ? "bg-[#00E575] text-[#09090B] border-[#00E575] shadow-[0_0_12px_rgba(0,229,117,0.35)] hover:bg-[#00C964]"
                : "bg-[#00E575]/10 text-[#00E575] border-[#00E575]/40 hover:bg-[#00E575]/20"
            }`}
          >
            <div className={`flex-shrink-0 h-9 w-9 rounded-lg flex items-center justify-center ${
              isDone ? "bg-[#09090B]/15" : "bg-[#00E575]/15"
            }`}>
              {uploading ? (
                <Loader2 size={18} className="animate-spin" />
              ) : isDone ? (
                <CheckCircle2 size={18} />
              ) : (
                <Camera size={18} />
              )}
            </div>
            <div className="flex-1 min-w-0 text-[13px] leading-tight font-semibold break-words">
              {label}
            </div>
            {isDone && (
              <span className="flex-shrink-0 inline-flex items-center justify-center min-w-[24px] h-6 px-1.5 rounded-full bg-[#09090B] text-[#00E575] text-[12px] font-bold font-mono">
                {existingCount}
              </span>
            )}
          </button>
          <button
            type="button"
            data-testid={(testid || `photo-btn-${category}`) + "-video"}
            onClick={() => videoRef.current?.click()}
            className="flex-shrink-0 min-h-[60px] w-14 rounded-xl border-2 border-[#00E575]/40 bg-[#00E575]/10 text-[#00E575] hover:bg-[#00E575]/20 transition active:scale-95 flex items-center justify-center"
            aria-label="Video aufnehmen"
            title="Video aufnehmen"
          >
            <Film size={20} />
          </button>
        </div>
        <input ref={photoRef} type="file" accept="image/*" capture="environment" onChange={handleFile("photo")} className="hidden input-raw" />
        <input ref={videoRef} type="file" accept="video/*" capture="environment" onChange={handleFile("video")} className="hidden input-raw" />
        <PhotoNoteModal
          open={modalOpen}
          file={pickedFile}
          mediaType={pickedType}
          projectId={projectId}
          category={category}
          defaultLabel={description || label || category}
          onClose={onModalClose}
          onUploaded={onModalUploaded}
        />
      </>
    );
  }

  // PILL variant (default): kompakt – Foto-Pille + kleiner Video-Button
  return (
    <>
      <div className="inline-flex items-center gap-1.5">
        <button
          type="button"
          data-testid={testid || `photo-btn-${category}`}
          onClick={() => photoRef.current?.click()}
          disabled={uploading}
          className={`${sizes[size]} inline-flex items-center justify-center rounded-lg font-semibold transition active:scale-95 disabled:opacity-50 whitespace-nowrap border-2 ${
            isDone
              ? "bg-[#00E575] text-[#09090B] border-[#00E575] shadow-[0_0_12px_rgba(0,229,117,0.45)] hover:bg-[#00C964]"
              : "bg-[#00E575]/10 text-[#00E575] border-[#00E575]/50 hover:bg-[#00E575]/20"
          }`}
        >
          {uploading ? (
            <Loader2 size={14} className="animate-spin" />
          ) : isDone ? (
            <CheckCircle2 size={15} />
          ) : (
            <Camera size={15} />
          )}
          <span>{label}</span>
          {isDone && (
            <span className={`ml-0.5 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#09090B] text-[#00E575] text-[12px] font-bold font-mono`}>
              {existingCount}
            </span>
          )}
        </button>
        <button
          type="button"
          data-testid={(testid || `photo-btn-${category}`) + "-video"}
          onClick={() => videoRef.current?.click()}
          className={`${size === "sm" ? "h-9 w-9" : size === "lg" ? "h-11 w-11" : "h-10 w-10"} rounded-lg border-2 border-[#00E575]/40 bg-[#00E575]/10 text-[#00E575] hover:bg-[#00E575]/20 transition active:scale-95 flex items-center justify-center`}
          aria-label="Video aufnehmen"
          title="Video aufnehmen"
        >
          <Film size={15} />
        </button>
      </div>
      <input ref={photoRef} type="file" accept="image/*" capture="environment" onChange={handleFile("photo")} className="hidden input-raw" />
      <input ref={videoRef} type="file" accept="video/*" capture="environment" onChange={handleFile("video")} className="hidden input-raw" />
      <PhotoNoteModal
        open={modalOpen}
        file={pickedFile}
        mediaType={pickedType}
        projectId={projectId}
        category={category}
        defaultLabel={description || label || category}
        onClose={onModalClose}
        onUploaded={onModalUploaded}
      />
    </>
  );
};
